// web/src/components/schedule/ScheduleItem.jsx
// One row on the Schedule board — a task or a lesson, told apart by a color
// spine rather than a badge, so a dense week column stays scannable.
//
// Tapping opens the row rather than acting on it — see ItemModal, which is
// where marking done, editing and deleting now live. It used to tick a task off
// on the spot, which is right on the Daily Board and wrong here, where the same
// tap is how you find out what the row is.
//
// The full variant also offers the row's people as filter chips. They sit
// outside the main button rather than inside it: a button can't nest, and
// "tick this off" and "show me only Ava" must not be the same tap.
//
// The name gets the whole width. The time used to sit in a right rail, which
// on a seven-column week left about 90px for a person and turned every row
// into "Theo Lin…". It leads the detail line underneath instead, where it is
// still the first thing read but is no longer competing with the name for the
// same 170px.

import { formatTime } from '../../lib/schedule';
import { colorClass } from '../../lib/lessonTypes';

// Color spine by what the block actually is, so a dense week column can be
// read at a glance. Tasks are always emerald; a lesson takes the color its
// barn chose for that lesson type (Settings → Lesson Types).
//
// This is the one place in the app where color means identity rather than
// state — the barn picked these, and they mean "jumping" or "hack". State is
// carried separately, by muting: a done row goes grey, the same grey that
// means "already happened" on a horse card.
//
// Color is never the only signal — the type is also written into the row's
// detail line, which is what a color-blind user or a printout relies on.
function spineClass(item) {
  if (item.kind === 'task') return 'bg-emerald-400';
  // Slate for a service, deliberately outside the barn's palette: a farrier
  // falling due is not a kind of lesson, and giving it a type color would put
  // it in a set it isn't in.
  if (item.kind === 'service') return 'bg-slate-400';
  return colorClass(item.lessonTypeColor, 'spine');
}

export default function ScheduleItem({
  item,
  variant = 'compact',
  busy = false,
  onActivate,
  onFilterPerson,
}) {
  const full = variant === 'full';
  const isTask = item.kind === 'task';
  const chips = full && onFilterPerson ? item.people ?? [] : [];

  const label = isTask
    ? item.done
      ? `Mark ${item.title} ${item.detail} not done`
      : `Mark ${item.title} ${item.detail} complete`
    : item.kind === 'service'
      ? `${item.title} due — open ${item.raw?.horseName ?? 'the horse'}`
      : `Edit lesson for ${item.title}`;

  // Time first, because it's what orders the day, then what the thing is and
  // who it's with.
  const detail = [item.time && formatTime(item.time), item.detail, item.person]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={full ? 'space-y-1' : ''}>
      <button
        type="button"
        onClick={onActivate}
        disabled={busy || item.muted}
        aria-label={label}
        aria-pressed={isTask ? item.done : undefined}
        className={
          'w-full text-left flex items-center gap-2 transition ' +
          // In a week column these are rows in one bordered list, not ten
          // bordered boxes inside a bordered card — that nesting flattened the
          // hierarchy and cost the padding the names needed.
          (full
            ? 'gap-3 p-4 rounded-xl border ' +
              (item.done ? 'bg-ink-50 border-ink-100 ' : 'bg-white border-ink-100 ')
            : 'px-2 py-1.5 ' + (item.done ? 'bg-ink-50 ' : '')) +
          (item.muted
            ? 'opacity-60 '
            : full
              ? 'hover:border-ink-200 active:scale-[0.99] '
              : 'hover:bg-ink-50 ')
        }
      >
        <span
          aria-hidden
          className={
            'shrink-0 rounded-full ' +
            (full ? 'w-1 h-10 ' : 'w-1 h-7 ') +
            spineClass(item) +
            (item.done ? ' opacity-40' : '')
          }
        />

        {isTask && (
          <span
            aria-hidden
            className={
              'shrink-0 rounded-full border-2 flex items-center justify-center ' +
              (full ? 'w-12 h-12 text-lg ' : 'w-5 h-5 text-[10px] ') +
              // Hunter, not emerald: emerald is a lesson-type color, and a
              // check in it would read as a type rather than as "done".
              (item.done ? 'bg-hunter border-hunter text-white' : 'border-ink-200')
            }
          >
            {item.done ? '✓' : ''}
          </span>
        )}

        <span className="flex-1 min-w-0">
          {/* The name wraps rather than truncates. Seven columns at the lg
              breakpoint are 131px wide, which is not enough for "Finnegan"
              once a spine and a checkbox have taken their share — and a
              barn board that says "Theo Lin…" is not a board. Two short
              lines beat one clipped one. The detail under it still
              truncates: the time leads, so what falls off the end is the
              trainer's name rather than when the thing happens. */}
          <span
            className={
              'block break-words ' +
              (full ? 'font-medium ' : 'text-sm font-medium leading-tight ') +
              // Grey says it's done. It used to also be struck through, on top
              // of a check and a grey background — four ways of saying one
              // thing, one of them a line through somebody's name.
              (item.done || item.muted ? 'text-ink-400' : '')
            }
          >
            {item.title}
          </span>
          {detail && (
            <span className={'block truncate text-ink-500 ' + (full ? 'text-sm' : 'text-xs')}>
              {detail}
            </span>
          )}
        </span>
      </button>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pl-4">
          {chips.map((p) => (
            <button
              key={p.ref}
              type="button"
              onClick={() => onFilterPerson(p.ref)}
              className="rounded-full border border-ink-100 px-2 py-0.5 text-xs text-ink-500 hover:text-ink-900 hover:border-ink-300"
            >
              {p.name}’s schedule
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
